import React, { useContext, useEffect, useRef, useState } from "react";
import { AccountContext } from "../../store/AccountContext";
import { Link, Navigate } from "react-router-dom";
import { Field, Form } from "react-final-form";
import {
  composeValidators,
  isEmail,
  isEmailRequired,
  isPasswordRequired,
} from "../../utils/validators";
import { getEmailSuggestion } from "../../utils/emailDomainCheck";
import { consumeReturnPath } from "../../utils/returnPath";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * PM5 T2 (money path, batch "signin") — converted from MUI v4 + `makeStyles`
 * to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** This is the email/password login form. The
 * conversion is styling-only: no handler, validator, state value, prop,
 * condition or DOM node moved. Acceptance bar is visual parity — every rule
 * below is the v4 rule it replaces, and each place a v5+ library default would
 * have changed the rendering is compensated for with a comment saying why.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `center` and `button` were declared in
 * the v4 `useStyles` object but never referenced from the JSX, so they rendered
 * nothing. Carrying them forward would be inventing styles, not porting them.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().form — a bare <div>, which has no `sx` prop.
const FormBody = styled("div")(({ theme }) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: theme.spacing(1),
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was implemented as
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaced that with `gap` on the container and **no** padding on the items
 * (guide §2, "Grid `spacing` implementation"). The two are only geometrically
 * identical when nothing else touches the container's box, so rather than
 * re-deriving that per container the port carries v4's own declarations
 * verbatim: `spacing` is omitted (v9 default 0 ⇒ `gap: 0px`) and each item
 * carries the 8px padding itself. Identical by construction, not by arithmetic
 * — and it keeps the 8px container overhang that the full-width children here
 * are laid out against.
 */
const v4GridContainerSx: SxProps<Theme> = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx: SxProps<Theme> = { padding: "8px" };

// was: useStyles().bodyText
const bodyTextSx: SxProps<Theme> = {
  color: "common.white",
  marginBottom: "10px",
  textAlign: "center",
};

// was: useStyles().validationError
const validationErrorSx: SxProps<Theme> = { marginBottom: 2 };

// was: useStyles().backdrop
const backdropSx: SxProps<Theme> = { zIndex: 100000000, color: "#fff" };

// was: useStyles().forgotPassword
const forgotPasswordSx: SxProps<Theme> = {
  color: "background.ffGreen",
  fontWeight: 400,
  textAlign: "center",
};

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted the `default`
// colour and made `primary` the default, which here would paint the label lime
// on the lime `typography.button` background — i.e. an invisible "Log In"
// button on the login page. Pinning the colour keeps it rendering as today.
const submitSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(1.5, 0, 2),
  color: "text.primary",
});

// was: useStyles().secButton. This one pins its own `color`, so the v5+ Button
// default-colour change above cannot reach its label.
const secButtonSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(1.5, 0, 2),
  borderColor: "#e9e9e9",
  color: "#e9e9e9",
  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },
  "&:hover": {
    borderColor: "#fff",
    color: "#fff",
  },
});

// was: useStyles().formField, merged with the Grid item padding above. The two
// descendant selectors are *element* selectors (`label`, `input`), not
// `.Mui*` ones, so the guide §1.2b class-prefix trap does not apply — they keep
// matching the v9 TextField's label and input.
//
// PARITY RESTORE (T2 visual gate) — `&&` instead of `&` on the label rule.
// The theme carries its own `MuiFormControl.styleOverrides.root` → `"& label"`
// (color `#171717`), and the TextField's FormControl root IS an ancestor of
// this label, so the two rules tie at (0,1,1) and only source order separates
// them. In v4 order went the file's way for free: JSS gave MUI's own sheet a
// lower index than any `makeStyles` sheet, so `.makeStyles-formField-N label`
// was emitted AFTER `.MuiFormControl-root label` and won. Emotion inserts in
// *render* order instead, and the FormControl is a CHILD of this Grid — so its
// class is emitted after this one and the theme's navy wins, on a navy card.
// Measured in Chromium on both themes' emitted CSS: label `rgb(255,255,255)`
// (v4) vs `rgb(23,23,23)` (v9, before this fix). `&&` = (0,2,1), which restores
// the v4 outcome by specificity rather than by luck of insertion order.
const formFieldItemSx: SxProps<Theme> = {
  padding: "8px",
  "&& label": {
    color: "common.white",
  },
  "& input": {
    backgroundColor: "#444",
    color: "#fff",
    borderColor: "#444",
  },
};

// The trailing `<Grid item xs={12} sm={12}>` sits OUTSIDE any `<Grid container>`
// (it is a sibling of the container, inside `FormBody`). In v4 that still
// emitted the item's `flex-basis`/`max-width` percentages; in v9 a Grid's width
// comes from CSS custom properties published by a container, so with no
// container the calc() is invalid and the element falls back to full width.
// Guide §2 row 3: port an orphan item as a Box carrying the literal v4
// declarations, never as a Grid.
const orphanGridItemSx: SxProps<Theme> = {
  boxSizing: "border-box",
  margin: 0,
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

const EmailSignIn = ({
  toggleLoginMethod,
}: {
  toggleLoginMethod: () => void;
}) => {
  const {
    loginWithUsernamePassword,
    loggedIn,
    loading,
    resetErrors,
    loginError,
  } = useContext(AccountContext);
  // Non-blocking "Did you mean?" hint when the email's domain looks like
  // a typo of a known provider (gmail, hotmail, etc.). Standardised with
  // /try signup. Deliberately no `isEmailDomainValid` blocking validator
  // here — that would lock out existing members whose stored email has
  // a typo-TLD (".con" etc.) but who are otherwise valid users.
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  // Captured once, the first render after login succeeds: the deep link the
  // member was heading for before the auth gate bounced them (#571). Reading
  // it consumes (clears) it, so the ref guard makes sure that happens exactly
  // once and the value is stable across the redirect's re-renders.
  const redirectTarget = useRef<string | null>(null);

  useEffect(() => {
    return () => resetErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    await loginWithUsernamePassword(email, password);
  };

  if (loggedIn) {
    if (redirectTarget.current === null) {
      redirectTarget.current = consumeReturnPath() || "/videos";
    }
    return <Navigate to={redirectTarget.current} replace />;
  }

  return (
    <>
      <Typography
        component="p"
        variant="body2"
        sx={bodyTextSx}
        style={{ marginBottom: "20px" }}
      >
        Log In with your Email and Password.
      </Typography>
      <Form onSubmit={onSubmit}>
        {(props) => (
          <form onSubmit={props.handleSubmit}>
            <FormBody>
              <Grid container sx={v4GridContainerSx}>
                <Grid size={{ xs: 12, sm: 12 }} sx={formFieldItemSx}>
                  {loginError && (
                    <Alert
                      sx={validationErrorSx}
                      severity="error"
                      variant="filled"
                    >
                      {loginError}
                    </Alert>
                  )}
                  <Field
                    name="email"
                    validate={composeValidators(isEmailRequired, isEmail)}
                  >
                    {({ input, meta }) => (
                      <>
                        {/* PARITY RESTORE: `variant="standard"` is v4's
                            TextField default. v5+ changed the default to
                            "outlined", which would swap the underline input for
                            a boxed one and route the theme's `MuiInput.underline`
                            overrides to a component that is no longer rendered.
                            `InputLabelProps` was removed in the v9 line; the
                            replacement is `slotProps.inputLabel`, same effect. */}
                        {/* `autoComplete="username"` — NOT "email". This is the
                            identifier half of a login pair, and password
                            managers key on username+current-password together;
                            "email" is the address-book hint used in checkout
                            forms and does not pair. Without any attribute at
                            all (the previous state) browsers fall back to
                            heuristics and will put whatever they have stored
                            for this origin here — which is how a saved mobile
                            number ends up in the Email box. */}
                        <TextField
                          fullWidth
                          variant="standard"
                          label="Email"
                          autoComplete="username"
                          {...input}
                          onBlur={(e) => {
                            input.onBlur(e);
                            setEmailSuggestion(getEmailSuggestion(input.value));
                          }}
                          onFocus={(e) => {
                            input.onFocus(e);
                            setEmailSuggestion(null);
                          }}
                          disabled={loading || loggedIn}
                          type="email"
                          placeholder="Email"
                          error={meta.error && meta.touched}
                          helperText={
                            meta.error && meta.touched ? meta.error : undefined
                          }
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                        {emailSuggestion && !meta.error && meta.touched && (
                          <Typography
                            variant="body2"
                            style={{
                              color: "#f5a623",
                              marginTop: 4,
                              fontSize: "0.8rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              input.onChange(emailSuggestion);
                              setEmailSuggestion(null);
                            }}
                          >
                            Did you mean <strong>{emailSuggestion}</strong>?
                            Click to use it.
                          </Typography>
                        )}
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }} sx={formFieldItemSx}>
                  {/* `autoComplete="current-password"` below, not
                      "new-password" — this is a sign-in, so a password manager
                      should offer the STORED password rather than generating
                      one. The signup and reset forms are the ones that want
                      "new-password". */}
                  <Field
                    name="password"
                    validate={composeValidators(isPasswordRequired)}
                  >
                    {({ input, meta }) => (
                      <TextField
                        fullWidth
                        variant="standard"
                        label="Password"
                        autoComplete="current-password"
                        {...input}
                        disabled={loading || loggedIn}
                        type="password"
                        placeholder="Password"
                        error={meta.error && meta.touched}
                        helperText={
                          meta.error && meta.touched ? meta.error : undefined
                        }
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                  <Link
                    to="/forgotpassword"
                    style={{
                      textDecoration: "underline",
                      textDecorationColor: "#99ca3c",
                    }}
                  >
                    <Typography variant="body2" sx={forgotPasswordSx}>
                      Forgot Your Password?
                    </Typography>
                  </Link>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                  {!loading && (
                    <Button
                      name="signIn"
                      fullWidth
                      size="medium"
                      sx={submitSx}
                      type="submit"
                    >
                      Log In
                    </Button>
                  )}
                  {loading && (
                    <Backdrop sx={backdropSx} open={true}>
                      <CircularProgress color="primary" />
                    </Backdrop>
                  )}
                </Grid>
              </Grid>
              <Box sx={orphanGridItemSx}>
                <Button
                  onClick={toggleLoginMethod}
                  fullWidth
                  sx={secButtonSx}
                  size="medium"
                  variant="outlined"
                  focusRipple={true}
                >
                  Switch to Mobile
                </Button>
              </Box>
            </FormBody>
          </form>
        )}
      </Form>
    </>
  );
};

export default EmailSignIn;
